import { useEffect } from 'react';
import { Alert, App, Col, Form, Input, InputNumber, Modal, Row, Select, TimePicker } from 'antd';
import type { Rule } from 'antd/es/form';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../api/client';
import {
  catalogApi,
  catalogKeys,
  type Branch,
  type BranchStatus,
  type Chain,
  type TableClass,
  type TableClassName,
} from './api';
import { tokens } from '../../theme';

const TIME_FORMAT = 'HH:mm';
const WIRE_TIME_FORMAT = 'HH:mm:ss';

const TABLE_CLASSES: readonly TableClassName[] = ['A', 'B', 'C'];

const TABLE_CLASS_LABELS: Record<TableClassName, string> = {
  A: 'Loại A — bàn nhỏ (1–2 khách)',
  B: 'Loại B — bàn vừa (3–4 khách)',
  C: 'Loại C — bàn lớn (5 khách trở lên)',
};

const STATUS_OPTIONS: { value: BranchStatus; label: string }[] = [
  { value: 'Active', label: 'Đang hoạt động' },
  { value: 'TemporarilyClosed', label: 'Tạm đóng cửa' },
  { value: 'Inactive', label: 'Ngừng hoạt động' },
];

interface FormValues {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  hours: [Dayjs, Dayjs];
  phone?: string;
  status: BranchStatus;
  tables: Record<TableClassName, { tableCount: number; seedDiningMinutes: number }>;
}

interface Props {
  /** Khác null là đang SỬA chi nhánh này */
  branch: Branch | null;
  /** Khác null là đang TẠO chi nhánh mới cho chuỗi này */
  chain: Chain | null;
  onClose: () => void;
}

const toTableMap = (
  classes: TableClass[],
): FormValues['tables'] =>
  Object.fromEntries(
    TABLE_CLASSES.map((name) => {
      const found = classes.find((x) => x.class === name);
      return [
        name,
        {
          tableCount: found?.tableCount ?? 0,
          // 45 phút là giá trị hạt giống của backend khi tạo chi nhánh; lặp lại ở đây
          // chỉ để ô nhập không trống nếu server trả thiếu loại bàn
          seedDiningMinutes: found?.seedDiningMinutes ?? 45,
        },
      ];
    }),
  ) as FormValues['tables'];

export function BranchFormModal({ branch, chain, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const isEditing = branch !== null;
  const open = isEditing || chain !== null;

  useEffect(() => {
    if (!open) return;

    if (branch !== null) {
      form.setFieldsValue({
        name: branch.name,
        address: branch.address,
        latitude: branch.latitude,
        longitude: branch.longitude,
        hours: [
          dayjs(branch.openTime, WIRE_TIME_FORMAT),
          dayjs(branch.closeTime, WIRE_TIME_FORMAT),
        ],
        phone: branch.phone ?? undefined,
        status: branch.status,
        tables: toTableMap(branch.tableClasses),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: 'Active',
        tables: toTableMap([]),
      });
    }
  }, [open, branch, form]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const [openTime, closeTime] = values.hours;

      const payload = {
        name: values.name,
        address: values.address,
        latitude: values.latitude,
        longitude: values.longitude,
        openTime: openTime.format(WIRE_TIME_FORMAT),
        closeTime: closeTime.format(WIRE_TIME_FORMAT),
        phone: values.phone?.trim() === '' ? null : (values.phone ?? null),
      };

      const tableClasses = TABLE_CLASSES.map((name) => ({
        class: name,
        tableCount: values.tables[name].tableCount,
        seedDiningMinutes: values.tables[name].seedDiningMinutes,
      }));

      // Tạo: số bàn đi cùng POST — một lệnh, lỗi bind/validate thì không có bản ghi chi nhánh.
      // Sửa: chi nhánh đã tồn tại nên vẫn PUT số bàn riêng; lỗi bước đó không đẻ bản ghi mới.
      if (branch !== null) {
        const saved = await catalogApi.updateBranch(branch.id, { ...payload, status: values.status });
        try {
          await catalogApi.setTableClasses(saved.id, tableClasses);
        } catch (cause) {
          throw new PartialSaveError(cause);
        }
        return saved;
      }

      return catalogApi.createBranch({ ...payload, chainId: chain!.id, tableClasses });
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: catalogKeys.branches });
      await queryClient.invalidateQueries({ queryKey: catalogKeys.chains });
      message.success(isEditing ? `Đã lưu ${saved.name}.` : `Đã tạo chi nhánh ${saved.name}.`);
      onClose();
    },
    onError: async (error) => {
      // Chi nhánh đã lưu nhưng số bàn thì chưa — làm mới danh sách để giao diện
      // phản ánh đúng thứ ĐÃ ghi, đừng để người dùng nhìn dữ liệu cũ
      if (error instanceof PartialSaveError) {
        await queryClient.invalidateQueries({ queryKey: catalogKeys.branches });
      }
    },
  });

  const errorText = describeError(mutation.error);

  return (
    <Modal
      open={open}
      width={640}
      title={isEditing ? `Sửa ${branch.name}` : `Thêm chi nhánh cho ${chain?.name ?? ''}`}
      okText={isEditing ? 'Lưu thay đổi' : 'Tạo chi nhánh'}
      cancelText="Hủy"
      confirmLoading={mutation.isPending}
      okButtonProps={{ disabled: mutation.isPending }}
      onCancel={() => {
        mutation.reset();
        onClose();
      }}
      onOk={() => {
        if (mutation.isPending) return;
        void form.submit();
      }}
      destroyOnHidden
    >
      {errorText !== null && (
        <Alert style={{ marginBottom: 16 }} type="error" showIcon message={errorText} />
      )}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => {
          if (mutation.isPending) return;
          mutation.mutate(values);
        }}
      >
        <Form.Item
          label="Tên chi nhánh"
          name="name"
          rules={[{ required: true, message: 'Chưa nhập tên chi nhánh.' }]}
        >
          <Input autoFocus maxLength={200} />
        </Form.Item>

        <Form.Item
          label="Địa chỉ"
          name="address"
          rules={[{ required: true, message: 'Chưa nhập địa chỉ.' }]}
        >
          <Input maxLength={400} />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Vĩ độ"
              name="latitude"
              // Khách bốc số phải đứng trong bán kính quanh tọa độ này (BR-501).
              // Sai tọa độ là khách đứng ngay cửa hàng vẫn bị từ chối.
              tooltip="Dùng để kiểm tra khách có ở gần chi nhánh khi bốc số."
              rules={[
                { required: true, message: 'Chưa nhập vĩ độ.' },
                {
                  type: 'number',
                  min: -90,
                  max: 90,
                  message: 'Vĩ độ phải nằm trong khoảng -90 tới 90.',
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Kinh độ"
              name="longitude"
              rules={[
                { required: true, message: 'Chưa nhập kinh độ.' },
                {
                  type: 'number',
                  min: -180,
                  max: 180,
                  message: 'Kinh độ phải nằm trong khoảng -180 tới 180.',
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Giờ mở cửa"
              name="hours"
              rules={[{ required: true, message: 'Chưa nhập giờ mở cửa và đóng cửa.' }]}
            >
              <TimePicker.RangePicker
                style={{ width: '100%' }}
                format={TIME_FORMAT}
                minuteStep={5}
                order={false}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Số điện thoại"
              name="phone"
              tooltip="Không bắt buộc. Nếu nhập thì phải là số Việt Nam hợp lệ."
              rules={phoneRules}
            >
              <Input maxLength={20} placeholder="VD: 028 3822 1234 hoặc 0912 345 678" inputMode="tel" />
            </Form.Item>
          </Col>
        </Row>

        {isEditing && (
          <Form.Item label="Trạng thái" name="status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        )}

        <div
          style={{
            marginTop: 4,
            padding: tokens.space.cardPadding,
            background: tokens.color.surface2,
            borderRadius: tokens.radius.card,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Số bàn theo loại</div>
          <div style={{ fontSize: 12, color: tokens.color.ink2, marginBottom: 12 }}>
            Hai con số này quyết định thời gian chờ báo cho khách. Khai sai thì dự báo sai theo.
          </div>

          {TABLE_CLASSES.map((name) => (
            <Row key={name} gutter={12} align="bottom">
              <Col span={12}>
                <Form.Item
                  label={TABLE_CLASS_LABELS[name]}
                  name={['tables', name, 'tableCount']}
                  rules={tableCountRules}
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={1} addonAfter="bàn" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Thời gian ăn dự kiến"
                  name={['tables', name, 'seedDiningMinutes']}
                  rules={seedDiningMinutesRules}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={480} step={1} addonAfter="phút" />
                </Form.Item>
              </Col>
            </Row>
          ))}
        </div>
      </Form>
    </Modal>
  );
}

/** Chi nhánh đã ghi xong nhưng bước lưu số bàn hỏng — cần nói khác hẳn lỗi thường. */
class PartialSaveError extends Error {
  constructor(readonly inner: unknown) {
    super('partial-save');
    this.name = 'PartialSaveError';
  }
}

function describeError(error: unknown): string | null {
  if (error === null || error === undefined) return null;

  if (error instanceof PartialSaveError) {
    const detail = describeError(error.inner) ?? '';
    return `Đã lưu thông tin chi nhánh, nhưng chưa lưu được số bàn. Đừng tạo lại chi nhánh — mở lại chi nhánh này và khai số bàn. ${detail}`;
  }

  if (error instanceof ApiError || error instanceof NetworkError) return error.message;

  return 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.';
}

/**
 * SĐT chi nhánh — KHÔNG bắt buộc, nhưng nhập thì phải là số Việt Nam hợp lệ.
 *
 * Bỏ hết khoảng trắng, dấu chấm, gạch ngang, ngoặc rồi chuẩn hóa tiền tố `+84`/`84` về `0`, cuối
 * cùng phải là `0` + 9–10 chữ số: phủ cả di động (10 số) lẫn cố định có mã vùng (10–11 số). Server
 * chỉ giới hạn độ dài cột (20 ký tự) chứ chưa kiểm định dạng, nên chặn ở đây để không lưu rác như
 * `abc` hay `0123` vào danh bạ chi nhánh.
 */
const phoneRules: Rule[] = [
  {
    validator: (_, value?: string) => {
      if (!value || value.trim() === '') return Promise.resolve();

      const digits = value.replace(/[\s.()-]/g, '');
      const local = digits.startsWith('+84')
        ? `0${digits.slice(3)}`
        : digits.startsWith('84') && digits.length >= 11
          ? `0${digits.slice(2)}`
          : digits;

      return /^0\d{9,10}$/.test(local)
        ? Promise.resolve()
        : Promise.reject(
            new Error('Số điện thoại không hợp lệ. Nhập số Việt Nam, ví dụ 028 3822 1234 hoặc 0912 345 678.'),
          );
    },
  },
];

const tableCountRules: Rule[] = [
  { required: true, message: 'Chưa nhập số bàn.' },
  { type: 'integer', message: 'Số lượng bàn phải là số nguyên' },
  { type: 'number', min: 0, message: 'Số bàn không được âm.' },
];

const seedDiningMinutesRules: Rule[] = [
  { required: true, message: 'Chưa nhập thời gian ăn dự kiến.' },
  { type: 'integer', message: 'Thời gian ăn dự kiến phải là số nguyên.' },
  { type: 'number', min: 1, max: 480, message: 'Thời gian ăn dự kiến phải từ 1 tới 480 phút.' },
];
